import { useState } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { createOrganization } from '../../api/organizationApi';
import { TRIAL_ONCE_RULE } from '../../constants/tiers';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';

/**
 * Создание организации (прежний интерфейс). Новый клуб всегда на FREE:
 * сервер не принимает тариф при создании (иначе можно было выбрать любой,
 * включая бессрочный ENTERPRISE). Платный тариф - потом, в подписке клуба:
 * пробный период один раз на клуб или оплата.
 */
export function CreateOrganizationScreen() {
  const { popScreen } = useTabNavigationStore();
  const switchToOrganization = useAuthStore((state) => state.switchToOrganization);
  const user = useAuthStore((state) => state.user);

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user?.id) {
      setError('Не удалось определить текущего пользователя');
      return;
    }
    if (!name.trim()) {
      setError('Введите название организации');
      return;
    }
    if (name.length > 200) {
      setError('Название слишком длинное (макс. 200 символов)');
      return;
    }

    try {
      setLoading(true);
      const org = await createOrganization({ user_id: user.id, name: name.trim() });
      await switchToOrganization(org.id);
      alert(`Организация "${org.name}" создана на тарифе FREE. Сменить тариф можно в подписке организации.`);
      popScreen();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось создать организацию');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', color: 'var(--text-hint)', marginBottom: '4px',
  };

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Создание организации"
        subtitle="Укажите название"
      />

      {error && (
        <Card>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>
        </Card>
      )}

      <Card>
        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
          Организация создаётся на тарифе FREE. Сменить тариф можно потом в подписке организации.
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
          {TRIAL_ONCE_RULE}
        </p>
      </Card>

      {/* Organization Name */}
      <Card>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Название организации</h3>
        <div>
          <label style={labelStyle}>Название</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Футбольная академия Спартак"
            disabled={loading}
            maxLength={200}
            style={inputStyle}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>Максимум 200 символов</p>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={() => popScreen()} variant="secondary" fullWidth disabled={loading}>
          Назад
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !name.trim()} fullWidth>
          {loading ? 'Создание...' : 'Создать организацию'}
        </Button>
      </div>
    </div>
  );
}
